import { CacheManager } from '@/util';
import {
  Emphasis,
  MedicalOption,
  MedicalOptionGroupCode,
  MedicalOptionGroups
} from '@/modules/surgicalEvent';
import { valueOfEnum } from '@ianesthesie-kit/core';
import axios from 'axios';
import { ID } from '@ianesthesie-kit/services';

type OptionsCache = {
  groups: {
    [key in MedicalOptionGroupCode]?: {
      optionIds: ID[];
    };
  };
  options: {
    [id: ID]: MedicalOption;
  };
};

export class OptionsCacheManager extends CacheManager<
  MedicalOptionGroups,
  OptionsCache
> {
  private static readonly KEY_MANAGER = {
    serialize: (item: MedicalOption) => `${item.code}_${item.emphasis}`,
    deserialize: (key: string) => {
      const toks = key.split('_');
      if (toks.length !== 2) {
        throw new Error(`Cannot deserialize key: ${key}`);
      }
      return {
        code: toks[0],
        emphasis: valueOfEnum(toks[1], Emphasis)
      };
    }
  };

  constructor() {
    super();
  }

  private get keyManager() {
    return OptionsCacheManager.KEY_MANAGER;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected denormalize(cache: OptionsCache): MedicalOptionGroups {
    // options cache is meant to be used with #get(filterFn)
    throw new Error('Unsupported operation');
  }

  protected fetchFresh(): Promise<MedicalOptionGroups> {
    return this.fetchOptionGroups();
  }

  protected initializeCache(): OptionsCache {
    return { options: {}, groups: {} };
  }

  protected normalize(optionGroups: MedicalOptionGroups): OptionsCache {
    let groups: OptionsCache['groups'] = {};
    let options: OptionsCache['options'] = {};

    Object.entries(optionGroups).forEach(([key, items]) => {
      const groupCode = valueOfEnum(key, MedicalOptionGroupCode, {
        caseSensitive: false,
        throwIfNotFound: true
      });
      const optionByIds = items.reduce(
        (acc, it) => ({
          ...acc,
          [this.keyManager.serialize(it)]: it
        }),
        {}
      );
      groups = {
        ...groups,
        [groupCode!]: {
          optionIds: Object.keys(optionByIds)
        }
      };
      options = {
        ...options,
        ...optionByIds
      };
    });

    return {
      groups,
      options
    };
  }

  private fetchOptionGroups(): Promise<MedicalOptionGroups> {
    return axios
      .get('/bff/doctor/patient/surgical-event/option')
      .then((response) => response.data.data);
  }
}
